import React, { useState } from 'react';
import { Button, MessageBar, MessageBarBody, Text } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { FindingDecision } from '../../../../models/Entities/Contracts/ContractReview';
import { ErrorType } from '../../../../models/HttpError';
import { errorText } from '../ContractReview.Functions';
import { useContractReviewStyles } from '../ContractReview.Styles';
import { ContractReviewDecisionDialog } from '../ContractReviewDecisionDialog/ContractReviewDecisionDialog.Component';

// Barra de decisión en bloque (US4, DEC-004): contador, seleccionar los visibles decidibles, aceptar/rechazar/volver a
// pendiente con comentario; un PATCH por lote de 200, todo o nada.

export interface ContractReviewBulkBarProps {
  selectedCount: number;
  selectableVisibleIds: number[];
  busy: boolean;
  progress?: { done: number; total: number };
  lastResult?: { updated: number; error?: ErrorType };
  onSelectAllVisible: (ids: number[]) => void;
  onClear: () => void;
  onDecide: (decision: FindingDecision, comment: string | null) => void;
}

export const ContractReviewBulkBar: React.FC<ContractReviewBulkBarProps> = props => {
  const { t } = useTranslation();
  const styles = useContractReviewStyles();
  const [pending, setPending] = useState<FindingDecision | undefined>(undefined);

  return (
    <div className={styles.section} role="region" aria-label={t('contractReview.bulk.selected', { count: props.selectedCount })}>
      <div className={styles.spaceBetween}>
        <div className={styles.row}>
          <Text className={styles.numbers} aria-live="polite">
            {t('contractReview.bulk.selected', { count: props.selectedCount })}
          </Text>
          <Button size="small" appearance="transparent" disabled={props.selectableVisibleIds.length === 0} onClick={() => props.onSelectAllVisible(props.selectableVisibleIds)}>
            {t('contractReview.bulk.selectAllVisible')}
          </Button>
          <Button size="small" appearance="transparent" disabled={props.selectedCount === 0} onClick={props.onClear}>
            {t('contractReview.bulk.clear')}
          </Button>
        </div>
        <div className={styles.row}>
          <Button size="small" appearance="primary" disabled={props.selectedCount === 0 || props.busy} onClick={() => setPending('Accepted')}>
            {t('contractReview.bulk.accept')}
          </Button>
          <Button size="small" disabled={props.selectedCount === 0 || props.busy} onClick={() => setPending('Rejected')}>
            {t('contractReview.bulk.reject')}
          </Button>
          <Button size="small" disabled={props.selectedCount === 0 || props.busy} onClick={() => setPending('Pending')}>
            {t('contractReview.bulk.reset')}
          </Button>
        </div>
      </div>

      {props.busy && props.progress && (
        <Text className={styles.muted} aria-live="polite">
          {t('contractReview.bulk.progress', props.progress)}
        </Text>
      )}
      {!props.busy && props.lastResult && !props.lastResult.error && (
        <MessageBar intent="success">
          <MessageBarBody>{t('contractReview.bulk.done', { count: props.lastResult.updated })}</MessageBarBody>
        </MessageBar>
      )}
      {!props.busy && props.lastResult?.error && (
        <MessageBar intent="error" role="alert">
          <MessageBarBody>{t('contractReview.bulk.error', { message: errorText(props.lastResult.error) ?? t('contractReview.errors.generic') })}</MessageBarBody>
        </MessageBar>
      )}

      <ContractReviewDecisionDialog
        open={pending !== undefined}
        decision={pending ?? 'Accepted'}
        count={props.selectedCount}
        busy={props.busy}
        onCancel={() => setPending(undefined)}
        onConfirm={comment => {
          props.onDecide(pending!, comment);
          setPending(undefined);
        }}
      />
    </div>
  );
};

export default ContractReviewBulkBar;
